import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X, ChevronDown, UserRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import RestoreMark from "@/components/RestoreMark";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/services", label: "Services" },
  { to: "/iv-therapy", label: "IV Therapy" },
  { to: "/memberships", label: "Memberships" },
  { to: "/about", label: "About" },
  { to: "/guide", label: "Restore Guide" },
  { to: "/shop", label: "Shop" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-40 glass-nav" data-testid="site-header">
      <div className="container-x flex items-center justify-between h-16 sm:h-20">
        <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
          <RestoreMark size={44} className="transition-transform duration-500 group-hover:rotate-[8deg]" />
          <div className="flex flex-col leading-none">
            <span className="font-display text-[22px] sm:text-2xl tracking-tight text-sage-800">Restore<span className="text-clay-500">.</span></span>
            <span className="hidden sm:block text-[10px] uppercase tracking-[0.32em] text-sage-700/60 mt-1">Wellness</span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-0.5 mx-auto">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.to.slice(1)}`}
              className={({ isActive }) =>
                `px-3 py-2 text-sm rounded-full transition-colors hover:text-sage-700 ${isActive ? "text-sage-700" : "text-sage-800/70"}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3 shrink-0">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="flex items-center gap-2 rounded-full border border-sage-600/20 pl-3 pr-2 py-1.5 hover:bg-sand-100">
                  <UserRound className="w-4 h-4" /> <span className="text-sm max-w-[110px] truncate">{user.name}</span>
                  <ChevronDown className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-2xl">
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">Admin Dashboard</DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => navigate("/portal")} data-testid="menu-portal">Member Portal</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/portal/book-iv")} data-testid="menu-book-iv">Book an IV</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="menu-logout">Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/login" className="text-sm text-sage-800/80 hover:text-sage-700" data-testid="member-login-link">Member Login</Link>
          )}
          <Button asChild className="rounded-full bg-sage-600 hover:bg-sage-700 text-white h-10 px-5 shadow-none" data-testid="header-book-btn">
            <Link to="/book/consult">Book Now</Link>
          </Button>
        </div>

        <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-sand-200 bg-white/95 backdrop-blur-xl" data-testid="mobile-menu">
          <div className="container-x py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="py-2.5 text-[15px] text-sage-800" data-testid={`mnav-${n.to.slice(1)}`}>{n.label}</Link>
            ))}
            <div className="h-px bg-sand-200 my-2" />
            {user ? (
              <>
                {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="py-2.5 text-[15px] text-sage-800">Admin</Link>}
                <Link to="/portal" onClick={() => setOpen(false)} className="py-2.5 text-[15px] text-sage-800">Member Portal</Link>
                <button className="py-2.5 text-left text-[15px] text-sage-800" onClick={async () => { await logout(); setOpen(false); navigate("/"); }}>Sign out</button>
              </>
            ) : (
              <Link to="/login" onClick={() => setOpen(false)} className="py-2.5 text-[15px] text-sage-800">Member Login</Link>
            )}
            <Button asChild className="rounded-full bg-sage-600 hover:bg-sage-700 text-white h-11 mt-2">
              <Link to="/book/consult" onClick={() => setOpen(false)}>Book Now</Link>
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
